import type { AppGroupSearchEntry } from '@openx/types/targeting/inventoryAndContent.ts';
import type { InvalidAppNameWithSuggestions, ValidationResultV2 } from '../../shared/BulkAdd/types.ts';
import { APP_GROUP_NAME_LIMIT } from '../../shared/InventoryAndContent/constants.ts';

export const DEFAULT_MAX_SUGGESTIONS = 5;

/**
 * Extracts the App Name from a stored targeting value, which may be either a bare name or an
 * "id:name" pair (id-prefixed values are produced once a name has been resolved/validated).
 */
export const extractAppNameFromValue = (value: string): string => {
  const colonIdx = value.indexOf(':');
  return colonIdx !== -1 ? value.substring(colonIdx + 1) : value;
};

export interface ValidateAppNamesV2Params {
  items: string[];
  existingItems?: string[];
  maxSuggestions?: number;
  nameLimit?: number;
  /**
   * Performs the actual backend search for the given (already trimmed, deduped) names and
   * returns a `Map` keyed by lowercased name. Transport-specific (Apollo `appGroupSearch` query
   * in oa-ui, REST `/appgroup/search` in unity/REST apps) — the caller normalizes the raw
   * response into the canonical `AppGroupSearchEntry` shape before returning it here.
   */
  searchByNames: (names: string[]) => Promise<Map<string, AppGroupSearchEntry>>;
}

/**
 * Transport-agnostic App Name Bulk Add validation with suggestions, shared between oa-ui
 * (GraphQL) and unity/REST apps. Performs a single backend round-trip (via `searchByNames`)
 * per call, then validates exact matches and builds suggestions for invalid names.
 */
export const validateAppNamesV2 = async ({
  items,
  existingItems = [],
  maxSuggestions = DEFAULT_MAX_SUGGESTIONS,
  nameLimit = APP_GROUP_NAME_LIMIT,
  searchByNames,
}: ValidateAppNamesV2Params): Promise<ValidationResultV2> => {
  const existingSet = new Set(existingItems.map(item => extractAppNameFromValue(item).toLowerCase()));
  const validItems: string[] = [];
  const processedItemsSet = new Set<string>();
  const invalidItemsWithSuggestions: InvalidAppNameWithSuggestions[] = [];
  const duplicates: string[] = [];

  const seenLower = new Set<string>();
  const uniqueNames: string[] = [];
  for (const raw of items) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const lower = trimmed.toLowerCase();
    if (seenLower.has(lower)) continue;
    seenLower.add(lower);
    uniqueNames.push(trimmed);
  }

  // Defense-in-depth: never exceed the backend per-request cap. Callers should guard
  // the count before reaching here, but bail out with an error result instead of
  // letting the request fail server-side.
  if (uniqueNames.length > nameLimit) {
    return {
      errors: [
        {
          error: `You can validate at most ${nameLimit} App Names at once.`,
          invalidItems: [],
        },
      ],
      validItems: [],
      invalidItemsWithSuggestions: [],
    };
  }

  const resultByInput =
    uniqueNames.length > 0 ? await searchByNames(uniqueNames) : new Map<string, AppGroupSearchEntry>();

  for (const item of items) {
    const trimmedItem = item.trim();
    if (!trimmedItem) continue;

    const lowerItem = trimmedItem.toLowerCase();

    if (existingSet.has(lowerItem) || processedItemsSet.has(lowerItem)) {
      duplicates.push(trimmedItem);
      continue;
    }

    processedItemsSet.add(lowerItem);

    const entry = resultByInput.get(lowerItem);
    if (entry?.match) {
      // Use input as canonical name (input-as-name decision); case-insensitive match means
      // backend matched lowercased equivalent, but we keep user's casing in stored value.
      validItems.push(`${entry.match}:${trimmedItem}`);
      continue;
    }

    const suggestions = (entry?.suggestions ?? []).slice(0, maxSuggestions).map(s => ({
      name: s.name,
      id: s.id,
      isAlreadyAdded: existingSet.has(s.name.toLowerCase()) || processedItemsSet.has(s.name.toLowerCase()),
    }));

    invalidItemsWithSuggestions.push({
      invalidName: trimmedItem,
      suggestions,
    });
  }

  const errors: Array<{ error: string; invalidItems: string[] }> = [];
  if (duplicates.length > 0) {
    errors.push({
      error: 'Duplicates.',
      invalidItems: duplicates,
    });
  }
  if (invalidItemsWithSuggestions.length > 0) {
    errors.push({
      error: 'Invalid App Names',
      invalidItems: invalidItemsWithSuggestions.map(item => item.invalidName),
    });
  }

  return { errors, validItems, invalidItemsWithSuggestions };
};
